import { useAtom } from 'jotai';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Form, Stack } from '@crossnokaye/ux-common';
import { PushDrawer } from '@crossnokaye/ux-common/ckds';

import { BulkEditView } from '@repo/sdk/models/components';

import { BottomStickyPortal } from '@/components/app-layout/components/bottom-sticky-portal';
import { DiscardChangesDialog } from '@/components/change-dialogs/discard-changes-dialog';
import { useEquipmentBulkEditAccess } from '@/features/equipment-browser/access/use-equipment-bulk-edit-access';

import { EquipmentBulkEditPushout } from './bulk-edit-pushout/equipment-bulk-edit-pushout';
import { EquipmentBulkEditTable } from './bulk-edit-table/equipment-bulk-edit-table';
import { EquipmentBulkEditActionBar } from './components/equipment-bulk-edit-action-bar';
import { EquipmentBulkEditHeader } from './components/equipment-bulk-edit-header';
import { EquipmentBulkEditSaveBar } from './components/equipment-bulk-edit-save-bar';
import { SaveChangesDialog } from './components/save-changes-dialog';
import { SyncDialog } from './components/sync-dialog';
import { useEquipmentBulkEditForm } from './hooks/use-equipment-bulk-edit-form';
import { useEquipmentBulkEditSave } from './hooks/use-equipment-bulk-edit-save';
import { EquipmentBulkEditPreviewPushout } from './preview-pushout/equipment-bulk-edit-preview-pushout';
import { pushOutModeAtom } from './state/bulk-edit-params.state';

interface EquipmentBulkEditViewProps {
  bulkEditView: BulkEditView;
  onClose: () => Promise<void>;
}

export const EquipmentBulkEditView = ({
  bulkEditView,
  onClose,
}: EquipmentBulkEditViewProps): React.ReactNode => {
  const { canUseMultiSelectEdit } = useEquipmentBulkEditAccess();
  // Push out state
  const [pushOutMode, setPushOutMode] = useAtom(pushOutModeAtom);
  const isPushOutOpen = pushOutMode !== null;

  // Selection state
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([]);
  const [selectedColumnIds, setSelectedColumnIds] = useState<string[]>([]);
  const [focusedColumnId, setFocusedColumnId] = useState<string | null>(null);
  const savedPreviewSelectionRef = useRef<string[]>([]);

  // Form
  const { form } = useEquipmentBulkEditForm({ rows: bulkEditView.config.rows });

  // Save/discard workflow (dialogs, mutation, handlers)
  const {
    dialogState,
    closeSaveDialog,
    cancelPending,
    dismissDialog,
    hasPendingSave,
    saveProgressByChangeId,
    saveComplete,
    progressStats,
    isValidating,
    settingChanges,
    dialogSettingChanges,
    handleCloseAttempt,
    handleDiscardAttempt,
    handleConfirmDiscard,
    handleReviewSave,
    handleSyncContinue,
    handleConfirmSave,
  } = useEquipmentBulkEditSave({
    columns: bulkEditView.config.columns,
    form,
    rows: bulkEditView.config.rows,
    onClose,
  });

  // Wrap closeSaveDialog to clear selection when a save was attempted
  const handleSaveDialogClose = useCallback(() => {
    const { hadSaveProgress } = closeSaveDialog();
    if (hadSaveProgress) {
      setSelectedDeviceIds([]);
      setSelectedColumnIds([]);
    }
  }, [closeSaveDialog]);

  // Derived values
  const hasSettings =
    bulkEditView.config.rows.length > 0 && bulkEditView.config.columns.some((col) => col.canEdit);
  const deviceCount = bulkEditView.config.rows.length;

  // Push out handlers
  const handleOpenEdit = () => {
    if (!canUseMultiSelectEdit) {
      return;
    }
    setPushOutMode('edit');
  };
  const handleClosePushOut = () => {
    if (pushOutMode === 'preview') {
      setSelectedDeviceIds(savedPreviewSelectionRef.current);
      savedPreviewSelectionRef.current = [];
    }
    setPushOutMode(null);
  };
  const handleOpenPreview = () => {
    savedPreviewSelectionRef.current = selectedDeviceIds;
    setSelectedDeviceIds([]);
    setPushOutMode('preview');
  };

  // Clear selected column IDs when push out closes
  useEffect(() => {
    if (!isPushOutOpen) {
      setSelectedColumnIds([]);
      setFocusedColumnId(null);
    }
  }, [isPushOutOpen]);

  // Push out content
  const pushOutContent =
    pushOutMode === 'preview' ? (
      <EquipmentBulkEditPreviewPushout
        onClose={handleClosePushOut}
        settingChanges={settingChanges}
      />
    ) : (
      <EquipmentBulkEditPushout
        columns={bulkEditView.config.columns}
        rows={bulkEditView.config.rows}
        selectedRowIds={selectedDeviceIds}
        onClose={handleClosePushOut}
        onFocusedColumnChange={setFocusedColumnId}
        onSelectedColumnIdsChange={setSelectedColumnIds}
      />
    );

  return (
    <Form {...form}>
      <div className="flex flex-col h-full overflow-hidden" data-component="EquipmentBulkEditView">
        <div className="shrink-0 bg-background border-b border-ckds-border-tier2 p-gutter-sm">
          <EquipmentBulkEditHeader
            deviceCount={deviceCount}
            isPushOutOpen={isPushOutOpen}
            onClose={handleCloseAttempt}
            title={bulkEditView.config.title ?? ''}
          />
        </div>

        <PushDrawer
          open={isPushOutOpen}
          onClose={handleClosePushOut}
          options={{ closeOnEscape: true, width: '450px', resizable: true, minWidth: '450px' }}
          drawerContent={pushOutContent}
        >
          <div className="flex flex-col h-full overflow-hidden">
            {/* Sticky action bar */}
            <Stack className="shrink-0 bg-background border-b border-ckds-border-tier2" gap="xs">
              <EquipmentBulkEditActionBar
                isPushOutOpen={isPushOutOpen}
                onEdit={handleOpenEdit}
                rowsSelected={selectedDeviceIds.length}
              />
            </Stack>

            {/* Main content area — flex-1 min-h-0 constrains height, overflow-auto enables scroll */}
            <div className="flex-1 min-h-0 overflow-auto">
              {hasSettings ? (
                <EquipmentBulkEditTable
                  table={bulkEditView.config}
                  selectedDeviceIds={selectedDeviceIds}
                  onSelectedDeviceIdsChange={setSelectedDeviceIds}
                  selectedColumnIds={selectedColumnIds}
                  focusedColumnId={focusedColumnId}
                />
              ) : (
                <div className="flex h-full items-center justify-center">
                  <p className="text-ckds-text-fg-tier3">No settings available to edit</p>
                </div>
              )}
            </div>

            <BottomStickyPortal>
              <EquipmentBulkEditSaveBar
                isPushOutOpen={isPushOutOpen}
                isValidating={isValidating}
                onCancel={handleDiscardAttempt}
                onPreview={handleOpenPreview}
                onReviewSave={handleReviewSave}
              />
            </BottomStickyPortal>
          </div>
        </PushDrawer>
      </div>

      <DiscardChangesDialog
        open={dialogState.type === 'discard'}
        onOpenChange={(open) => !open && dismissDialog()}
        onConfirm={handleConfirmDiscard}
      />

      <SaveChangesDialog
        open={dialogState.type === 'save'}
        onOpenChange={(open) => !open && handleSaveDialogClose()}
        settingChanges={dialogSettingChanges}
        hasPendingSave={hasPendingSave}
        saveProgressByChangeId={saveProgressByChangeId}
        saveComplete={saveComplete}
        progressStats={progressStats}
        onClose={handleSaveDialogClose}
        onCancelPending={cancelPending}
        onConfirm={handleConfirmSave}
      />

      <SyncDialog
        open={dialogState.type === 'sync'}
        onOpenChange={(open) => {
          if (!open && dialogState.type === 'sync') dismissDialog();
        }}
        driftedSettings={dialogState.type === 'sync' ? dialogState.driftedSettings : []}
        columns={bulkEditView.config.columns}
        rows={bulkEditView.config.rows}
        onClose={dismissDialog}
        onContinue={handleSyncContinue}
      />
    </Form>
  );
};
